import { useEffect, useMemo, useState, Fragment, type CSSProperties } from "react";
import { trpc } from "../../lib/trpc";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { type Formula, computeFormulaValues, formulaToString } from "../../lib/formFormula";
import { TableFieldInput } from "./TableFieldInput";
import { type TableColumn, type TableRow } from "../../lib/formTable";
import { type FieldStyle, dividerStyles, cardStyles } from "../../lib/formStyle";
import "../../pages/Forms.css";

/** Snapshot/template alan şekli (backend toFieldShape ile ayna). */
export interface FieldShape {
  id: string;
  type: string;
  label: string;
  help: string | null;
  placeholder: string | null;
  required: boolean;
  options: string[];
  minVal: number | null;
  maxVal: number | null;
  unit: string | null;
  colSpan: number;
  sortOrder?: number;
  refKey?: string;
  formula?: Formula | null;
  columns?: TableColumn[] | null;
  colStart?: number | null;
  rowStart?: number | null;
  rowSpan?: number | null;
  style?: FieldStyle | null;
}

/** Alanın ızgara hücresi: açık konum varsa (serbest yerleşim) oraya, yoksa eski auto-flow (span).
 *  Span savunmacı kısılır; kart/dikey-çizgi rowSpan kadar satır kaplar. */
function cellStyle(f: FieldShape): CSSProperties {
  const rowSpan = f.rowSpan && f.rowSpan > 1 ? f.rowSpan : 1;
  const rowSpanCss = rowSpan > 1 && (f.type === "card" || (f.type === "divider" && f.style?.orientation === "v")) ? ` / span ${rowSpan}` : "";
  if (f.colStart == null || f.rowStart == null) return { gridColumn: `span ${Math.min(f.colSpan, 12)}` };
  const col = Math.min(11, Math.max(0, f.colStart));
  const span = Math.min(f.colSpan, 12 - col);
  return { gridColumn: `${col + 1} / span ${Math.max(1, span)}`, gridRow: `${f.rowStart + 1}${rowSpanCss}` };
}

/** Hesaplanan değeri TR formatla (null → "—"). */
function fmtComputed(n: number | null | undefined): string {
  return n == null ? "—" : new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 6 }).format(n);
}

const NONE_SHIFT = "__none__";

function todayStr(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function toMin(hhmm: string): number { const [h, m] = hhmm.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); }
/** Şu anki duvar saatine göre aktif vardiyayı bul (gece-aşımı destekli) — yalnız UI ön-seçimi. */
function pickCurrentShift(shifts: Array<{ id: string; startTime: string; endTime: string }>): string {
  const d = new Date();
  const now = d.getHours() * 60 + d.getMinutes();
  for (const s of shifts) {
    const a = toMin(s.startTime), b = toMin(s.endTime);
    const inRange = a <= b ? now >= a && now < b : now >= a || now < b;
    if (inRange) return s.id;
  }
  return "";
}

interface Props {
  open: boolean;
  /** Yeni doldurma için yayınlanmış form id'si. */
  templateId: string | null;
  /** Mevcut kaydı düzenlemek için gönderim id'si. */
  submissionId: string | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
  /** Önizleme modu: builder'ın KAYDEDİLMEMİŞ alanlarını doğrudan göster; DB'den çekmez, Kaydet yok. */
  preview?: boolean;
  previewFields?: FieldShape[];
  previewName?: string;
  previewDescription?: string | null;
}

export function FormFillDrawer({ open, templateId, submissionId, onClose, onSaved, preview = false, previewFields, previewName, previewDescription }: Props) {
  const isEdit = !!submissionId && !preview;
  const utils = trpc.useUtils();

  const tplQ = trpc.form.template.get.useQuery({ id: templateId! }, { enabled: open && !isEdit && !!templateId && !preview, staleTime: 0 });
  const subQ = trpc.form.submission.get.useQuery({ id: submissionId! }, { enabled: open && isEdit, staleTime: 0 });
  const wsQ = trpc.settings.workSchedule.get.useQuery(undefined, { enabled: open && !isEdit, staleTime: 5 * 60_000 });
  const profQ = trpc.settings.companyProfile.get.useQuery(undefined, { enabled: open, staleTime: 5 * 60_000 });
  const activeShifts = useMemo(() => (wsQ.data?.shifts ?? []).filter((s) => s.isActive), [wsQ.data]);

  const fillMut = trpc.form.submission.fill.useMutation();

  const [date, setDate] = useState(todayStr());
  const [shiftId, setShiftId] = useState<string>("");
  // Düzenlemede kaydın GERÇEK vardiyası (upsert anahtarı) — tarihten yeniden çözülmemeli.
  const [editShiftId, setEditShiftId] = useState<string | null>(null);
  const [data, setData] = useState<Record<string, unknown>>({}); // string|boolean + tablo satır dizileri
  const [error, setError] = useState<string | null>(null);

  // Kaynak alanlar + form meta.
  const fields: FieldShape[] = preview ? (previewFields ?? []) : isEdit ? (subQ.data?.fields ?? []) : (tplQ.data?.fields ?? []);
  // Hesaplanan (formül) alanların CANLI değerleri — kullanıcı girdileri (data) değiştikçe türetilir (state DEĞİL → döngü yok).
  const computedValues = useMemo(() => computeFormulaValues(fields, data), [fields, data]);
  const labelByRef = useMemo(() => { const m = new Map<string, string>(); for (const f of fields) if (f.refKey) m.set(f.refKey, f.label); return m; }, [fields]);
  const formName = preview ? previewName : isEdit ? subQ.data?.formName : tplQ.data?.name;
  const formDescription = preview ? (previewDescription ?? null) : isEdit ? subQ.data?.formDescription : tplQ.data?.description;
  const effectiveTemplateId = isEdit ? subQ.data?.formTemplateId : templateId;

  // Antet (letterhead) — dolduran tesisin firma künyesi.
  const prof = profQ.data;
  const logo = prof?.logoUrl ? new URL(prof.logoUrl, window.location.origin).href : null;
  const companyMeta = [
    prof?.address,
    [prof?.taxOffice ? `VD: ${prof.taxOffice}` : "", prof?.taxNumber ? `VKN: ${prof.taxNumber}` : ""].filter(Boolean).join(" · "),
    prof?.phone ? `Tel: ${prof.phone}` : "",
    prof?.email ?? "",
  ].filter(Boolean).join(" · ");

  // Yükleme → state ilkle.
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (isEdit) {
      if (subQ.data) {
        const d: Record<string, unknown> = {};
        for (const f of subQ.data.fields as FieldShape[]) {
          if (f.type === "heading" || f.type === "note" || f.type === "divider" || f.type === "card") continue; // yapısal → değer taşımaz
          if (f.formula) continue; // hesaplanan alan → türetilir, state'e alınmaz
          const v = (subQ.data.data as Record<string, unknown>)[f.id];
          if (f.type === "table") { d[f.id] = Array.isArray(v) ? v : []; continue; } // tablo → satır dizisi
          d[f.id] = f.type === "boolean" ? v === true || v === "true" : v == null ? "" : String(v);
        }
        setData(d);
        setDate(new Date(subQ.data.filledForDate).toISOString().slice(0, 10));
        setEditShiftId(subQ.data.shiftId);
      }
    } else {
      setData({});
      setDate(todayStr());
    }
  }, [open, isEdit, subQ.data]);

  // Yeni doldurmada vardiyayı saate göre ön-seç.
  useEffect(() => {
    if (open && !isEdit && activeShifts.length > 0 && !shiftId) {
      setShiftId(pickCurrentShift(activeShifts) || activeShifts[0]!.id);
    }
  }, [open, isEdit, activeShifts, shiftId]);
  useEffect(() => { if (!open) { setShiftId(""); setEditShiftId(null); } }, [open]);

  function setVal(id: string, v: unknown) { setData((s) => ({ ...s, [id]: v })); }

  function validate(): string | null {
    for (const f of fields) {
      if (f.type === "heading" || f.type === "note" || f.type === "divider" || f.type === "card") continue;
      if (f.formula) continue; // hesaplanan alan → kullanıcı girmez, sunucu doğrular
      if (f.type === "table") {
        const rows = Array.isArray(data[f.id]) ? (data[f.id] as TableRow[]) : [];
        if (f.required && rows.length === 0) return `"${f.label}" için en az bir satır girin.`;
        for (let ri = 0; ri < rows.length; ri++) {
          for (const c of f.columns ?? []) {
            const cv = rows[ri]?.[c.key];
            const cEmpty = cv == null || cv === "";
            if (c.required && c.type !== "boolean" && cEmpty) return `"${f.label}" · satır ${ri + 1}: "${c.label}" zorunlu.`;
            if (!cEmpty && c.type === "number" && !Number.isFinite(Number(String(cv).replace(",", ".")))) return `"${f.label}" · satır ${ri + 1}: "${c.label}" sayı olmalı.`;
          }
        }
        continue;
      }
      const v = data[f.id];
      const empty = v === undefined || v === "" || v === null;
      if (f.required && f.type !== "boolean" && empty) return `"${f.label}" zorunlu.`;
      if (empty || f.type === "boolean") continue;
      if (f.type === "number") {
        const n = Number(String(v).replace(",", "."));
        if (!Number.isFinite(n)) return `"${f.label}" sayı olmalı.`;
        if (f.minVal != null && n < f.minVal) return `"${f.label}" en az ${f.minVal} olmalı.`;
        if (f.maxVal != null && n > f.maxVal) return `"${f.label}" en fazla ${f.maxVal} olmalı.`;
      }
      if (f.type === "select" && !f.options.includes(String(v))) return `"${f.label}" için geçerli bir seçenek seçin.`;
    }
    return null;
  }

  async function save() {
    const v = validate();
    if (v) { setError(v); return; }
    if (!effectiveTemplateId) return;
    setError(null);
    try {
      await fillMut.mutateAsync({
        formTemplateId: effectiveTemplateId,
        filledForDate: date,
        // Edit: kaydın gerçek vardiyası (tarihten yeniden çözme yok → doğru kaydı günceller).
        shiftId: isEdit ? editShiftId : (shiftId === NONE_SHIFT || shiftId === "" ? null : shiftId),
        data,
      });
      await utils.form.submission.list.invalidate();
      if (isEdit && submissionId) await utils.form.submission.get.invalidate({ id: submissionId });
      onSaved(isEdit ? "Kayıt güncellendi." : "Form kaydedildi.");
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const loading = !preview && ((isEdit && subQ.isLoading) || (!isEdit && tplQ.isLoading));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={preview ? "Form Önizleme" : isEdit ? "Kaydı Düzenle" : "Form Doldur"}
      subtitle={formName ?? undefined}
      fullScreen
      dismissible={preview}
      footer={
        preview ? (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, width: "100%" }}>
            <span style={{ fontSize: 12, color: "var(--neutral-500)", display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="eye" size={14} />Önizleme — girişler kaydedilmez</span>
            <button type="button" className="btn-md primary" onClick={onClose}>Kapat</button>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, width: "100%" }}>
            <button type="button" className="btn-md ghost" onClick={onClose}>Vazgeç</button>
            <button type="button" className="btn-md primary" onClick={save} disabled={fillMut.isPending || loading}>
              <Icon name="check" size={14} />{fillMut.isPending ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        )
      }
    >
      {loading ? (
        <div className="empty-state"><p className="ttl">Yükleniyor…</p></div>
      ) : fields.length === 0 ? (
        <div className="empty-state"><p className="ttl">Form bulunamadı</p><p className="sub">Bu form artık mevcut değil ya da alanı yok.</p></div>
      ) : (
        <div className="ff-doc">
          {error ? <div className="form-banner-err" role="alert" style={{ marginBottom: 14 }}><Icon name="alertTriangle" size={14} />{error}</div> : null}

          {/* ── Antet: SOLDA firma künyesi · SAĞDA form adı + açıklaması (sağa yaslı) ── */}
          <div className="ff-letterhead">
            <div className="ff-lh-left">
              {logo ? (
                <img className="ff-logo" src={logo} alt={prof?.name ?? "Firma logosu"} />
              ) : (
                <div className="ff-logo ph" aria-hidden="true"><Icon name="building" size={24} /></div>
              )}
              <div className="ff-company">
                <div className="ff-company-name">{prof?.name || "Firma Bilgisi Tanımsız"}</div>
                {companyMeta ? <div className="ff-company-meta">{companyMeta}</div> : null}
              </div>
            </div>
            <div className="ff-lh-right">
              <h3 className="ff-form-title">{formName}</h3>
              {formDescription ? <p className="ff-form-desc">{formDescription}</p> : null}
            </div>
          </div>

          {/* ── Tarih + Vardiya: minimal, header'ın hemen altında, sağa yaslı, yan yana ── */}
          <div className="ff-metabar">
            <div className="ff-meta-item">
              <label className="ff-meta-lbl" htmlFor="ff-date">Tarih <span className="req">*</span></label>
              {isEdit ? (
                <div className="ff-meta-ro" aria-readonly="true">{new Intl.DateTimeFormat("tr-TR").format(new Date(date))}</div>
              ) : (
                <input id="ff-date" className="form-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              )}
            </div>
            <div className="ff-meta-item">
              <label className="ff-meta-lbl" htmlFor="ff-shift">Vardiya</label>
              {isEdit ? (
                <div className="ff-meta-ro" aria-readonly="true">{subQ.data?.shiftName ?? "Vardiya yok"}</div>
              ) : (
                <select id="ff-shift" className="form-select" value={shiftId} onChange={(e) => setShiftId(e.target.value)}>
                  {activeShifts.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.startTime}–{s.endTime})</option>)}
                  <option value={NONE_SHIFT}>Vardiya yok / Genel</option>
                </select>
              )}
            </div>
          </div>

          {/* ── Alanlar — 12-kolon ızgara, tip-bazlı render ── */}
          <div className="ff-grid">
            {fields.map((f) => {
              if (f.type === "card") { // grup kutusu — arka planda; başlık AYRI öğe (z-index üstte) → alan üstünü örtmesin
                const cs = cardStyles(f.style);
                const titlePos: CSSProperties = f.colStart != null && f.rowStart != null
                  ? { gridColumn: `${Math.min(11, f.colStart) + 1} / span ${f.colSpan}`, gridRow: `${f.rowStart + 1}` }
                  : { gridColumn: `span ${f.colSpan}` };
                return (
                  <Fragment key={f.id}>
                    <div className="ff-card" style={{ ...cellStyle(f), ...cs }} />
                    {f.label ? <div className="ff-card-title" style={{ ...titlePos, paddingLeft: (f.style?.padding ?? 12) + 2, paddingTop: (f.style?.padding ?? 12) - 4 }}>{f.label}</div> : null}
                  </Fragment>
                );
              }
              if (f.type === "divider") { const dv = dividerStyles(f.style); return (
                <div key={f.id} className="ff-divider" style={{ ...cellStyle(f), ...dv.wrap }}><div style={dv.line} /></div>
              ); }
              if (f.type === "heading") return (
                <div key={f.id} className="ff-heading" style={cellStyle(f)}>
                  <span className="ff-heading-t">{f.label}</span>
                  {f.help ? <span className="ff-heading-h">{f.help}</span> : null}
                </div>
              );
              if (f.type === "note") return (
                <div key={f.id} className="ff-note" style={cellStyle(f)}>
                  <Icon name="fileText" size={14} />
                  <div><span className="ff-note-t">{f.label}</span>{f.help ? <span className="ff-note-h">{f.help}</span> : null}</div>
                </div>
              );
              if (f.type === "table") return (
                <div key={f.id} className="ff-field" style={cellStyle(f)}>
                  <label className="lbl">{f.label}{f.required ? <span className="req"> *</span> : null}</label>
                  <TableFieldInput columns={f.columns ?? []} rows={Array.isArray(data[f.id]) ? (data[f.id] as TableRow[]) : []} onChange={(rows) => setVal(f.id, rows)} />
                  {f.help ? <span className="help">{f.help}</span> : null}
                </div>
              );
              if (f.type === "number" && f.formula) return (
                <div key={f.id} className="ff-field" style={cellStyle(f)}>
                  <label className="lbl">
                    {f.label}{f.unit ? <span style={{ color: "var(--neutral-500)", fontWeight: 400 }}> ({f.unit})</span> : null}
                    <span className="ff-computed-badge" title="Hesaplanan alan (formül)">ƒ hesaplanan</span>
                  </label>
                  <div className="ff-computed-val">{fmtComputed(computedValues[f.id])}{f.unit ? <span className="unit"> {f.unit}</span> : null}</div>
                  <span className="help">= {formulaToString(f.formula, (ref) => labelByRef.get(ref) ?? "?")}</span>
                </div>
              );
              const fid = `ff-${f.id}`;
              const val = data[f.id];
              const labelEl = (
                <label className="lbl" htmlFor={fid}>
                  {f.label}{f.type === "number" && f.unit ? <span style={{ color: "var(--neutral-500)", fontWeight: 400 }}> ({f.unit})</span> : null}
                  {f.required ? <span className="req"> *</span> : null}
                </label>
              );
              return (
                <div key={f.id} className="ff-field" style={cellStyle(f)}>
                  {f.type === "boolean" ? (
                    <label className="ff-check">
                      <input id={fid} type="checkbox" checked={val === true} onChange={(e) => setVal(f.id, e.target.checked)} />
                      <span>{f.label}{f.required ? <span className="req"> *</span> : null}</span>
                    </label>
                  ) : f.type === "select" ? (
                    <>
                      {labelEl}
                      <select id={fid} className="form-select" value={typeof val === "string" ? val : ""} onChange={(e) => setVal(f.id, e.target.value)}>
                        <option value="">— Seç —</option>
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </>
                  ) : f.type === "textarea" ? (
                    <>
                      {labelEl}
                      <textarea id={fid} className="form-textarea" rows={3} value={typeof val === "string" ? val : ""} onChange={(e) => setVal(f.id, e.target.value)} placeholder={f.placeholder ?? undefined} />
                    </>
                  ) : (
                    <>
                      {labelEl}
                      <input
                        id={fid}
                        className="form-input"
                        type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "time" ? "time" : "text"}
                        step={f.type === "number" ? "any" : undefined}
                        inputMode={f.type === "number" ? "decimal" : undefined}
                        min={f.type === "number" && f.minVal != null ? f.minVal : undefined}
                        max={f.type === "number" && f.maxVal != null ? f.maxVal : undefined}
                        value={typeof val === "string" ? val : ""}
                        onChange={(e) => setVal(f.id, e.target.value)}
                        placeholder={f.placeholder ?? undefined}
                      />
                    </>
                  )}
                  {f.type !== "boolean" && f.help ? <span className="help">{f.help}</span> : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Drawer>
  );
}
